import { _ } from "../polyglot/polyglot.js";
import { timesIcon } from "../elements/icons/index.js";
import { widget } from "@foretagsplatsen/widgetjs";
import AndSpecification from "../../models/specification/AndSpecification.js";
import button from "@foretagsplatsen/finsit-design-system/widget-js/elements/button";
import DimensionSpecification from "../../models/dimensionSpecification/DimensionSpecification.js";
import jQuery from "jquery";
import NotSpecification from "../../models/specification/NotSpecification.js";
import OrSpecification from "../../models/specification/OrSpecification.js";
import popoverActionButtonWidget from "./popoverActionButtonWidget.js";
import SearchFieldWidget from "./SearchFieldWidget/SearchFieldWidget.js";
import TextRepresentationDimensionSpecificationVisitor from "../../models/dimensionSpecification/visitors/TextRepresentationDimensionSpecificationVisitor.js";
import TreeWidget from "./tree/TreeWidget/TreeWidget.js";

/**
 * Dimension picker, displays a list of selected dimensions and a list of excluded dimensions together with a
 * tree in a popover to include or exclude new dimensions to the selection.
 *
 * @param {dimension} spec.dimensionTree - Tree of dimensions the user can
 *   pick dimensions from
 * @param {specification} [spec.dimensionSpecification] - Initial dimension specification
 *   selected dimensions. The specification should be of the form (dim_1 | ... | dim_N) & !(dim_N+1 | ... | dim_N+M)
 *   to be editable
 * @param {boolean} [spec.isNestedPopover] - Whether the button
 *   opening a new popover is already included in another
 *   popover. This is used to get the proper position of the new
 *   popover
 */
const multipleDimensionsPickerWidget = widget.subclass((that, my) => {
	my.initialize = function ({ dimensionTree, dimensionSpecification } = {}) {
		my.super(...arguments);
		my.dimensionTree = dimensionTree;
		my.dimensionSpecification = dimensionSpecification;
	};

	my.initializeSubwidgets = function ({ isNestedPopover = false } = {}) {
		my.super(...arguments);

		my.popover = popoverActionButtonWidget({
			label: _("Add dimension"),
			popoverHeader: _("Select dimensions"),
			popoverContent: my.renderDimensionTreeOn,
			popoverFooter: my.renderFooterOn,
			isNestedPopover,
		});

		my.tree = new TreeWidget({
			data: [my.dimensionTree],
			getLabel: function (dimension) {
				return dimension.getLabel();
			},
			canBeSelected: my.canSelectDimension,
			autoOpen: 1,
		});

		my.tree.onSelectionChange.register((dimension) => {
			const moveButton = jQuery(".popover .footer .btn-primary").get(0);

			if (dimension) {
				jQuery(moveButton).removeAttr("disabled");
			} else {
				jQuery(moveButton).attr("disabled", "disabled");
			}
		});

		my.searchField = new SearchFieldWidget({
			inputId: my.nextId(),
			inputClass: "form-control search-input",
		});

		my.searchField.applySearchOn(my.tree);
	};

	that.onDimensionsSelected = my.events.createEvent();

	that.getIncludedDimensions = function () {
		return my.dimensionTree.includedDimensions(my.dimensionSpecification);
	};

	that.getExcludedDimensions = function () {
		return my.dimensionTree.excludedDimensions(my.dimensionSpecification);
	};

	that.updateDimensionTree = function (dimensionTree) {
		if (my.dimensionTree === dimensionTree) {
			return;
		}
		that.clearSelection();
		my.dimensionTree = dimensionTree;
		my.tree.setData([dimensionTree]);
	};

	that.getDimensionSpecification = function () {
		return my.dimensionSpecification;
	};

	that.clearSelection = function () {
		my.dimensionSpecification = null;
	};

	/**
	 * Return a string representing the dimension specification.
	 */
	that.selectionLabel = function () {
		const visitor = new TextRepresentationDimensionSpecificationVisitor({
			dimensionTree: my.dimensionTree,
		});
		return my.dimensionSpecification.accept(visitor);
	};

	that.pathString = function (dimension) {
		return my.dimensionTree.pathNameToChildId(dimension.getId());
	};

	that.hasSelectedDimensions = function () {
		return !!my.dimensionSpecification;
	};

	that.renderContentOn = function (html) {
		html.div(
			{
				klass: "dimensions-picker",
				"data-test": "add-dimension",
				"data-help": "dimensions-picker",
			},
			my.renderSelectedDimensionsOn,
			my.renderPickerOn,
		);
	};

	my.renderSelectedDimensionsOn = function (html) {
		if (!my.canEditDimensionSpecification()) {
			html.span(_("Dimension filtering is too complex to be edited"));
			return;
		}

		html.ul(my.renderDimensionsOn);
	};

	my.renderDimensionsOn = function (html) {
		that.getIncludedDimensions().forEach((dim) =>
			my.dimensionRenderer(dim, dim.getName())(html),
		);

		that.getExcludedDimensions().forEach((dim) =>
			my.dimensionRenderer(
				dim,
				_("excluding {label}", { label: dim.getName() }),
			)(html),
		);
	};

	my.dimensionRenderer = function (dimension, label) {
		return (html) =>
			html.li(
				{ klass: "block" },
				label,
				button(
					{
						as: "span",
						onClick: () => my.unselectDimension(dimension),
					},
					timesIcon({
						class: "remove-list-item fa-large",
					}),
				),
			);
	};

	my.renderPickerOn = function (html) {
		if (!my.canEditDimensionSpecification()) {
			html.button(
				{ klass: "btn btn-block btn-danger" },
				_("Reset dimensions"),
			).click(() => {
				my.dimensionSpecification = null;
				that.update();
				that.onDimensionsSelected.trigger(null);
			});

			return;
		}

		html.render(my.popover);
	};

	my.renderFooterOn = function (html) {
		return html.div({ class: "text-center" }, (html) => {
			html.a(
				{ class: "btn btn-primary", "data-test": "include" },
				_("Include"),
			).click(() => {
				my.popover.hide();
				my.includeDimension();
			});

			html.a({ class: "btn btn-danger" }, _("Exclude")).click(() => {
				my.popover.hide();
				my.excludeDimension();
			});
		});
	};

	my.renderDimensionTreeOn = function (html) {
		html.div({ klass: "form-group" }, my.searchField);
		html.render(my.tree);
	};

	my.canEditDimensionSpecification = function () {
		return (
			!my.dimensionSpecification ||
			my.dimensionSpecification.canBeEdited()
		);
	};

	my.canSelectDimension = function (dimension) {
		return (
			!that.getIncludedDimensions().includes(dimension) &&
			!that.getExcludedDimensions().includes(dimension)
		);
	};

	my.unselectDimension = function (dimension) {
		const includedDimensions = that
			.getIncludedDimensions()
			.filter((item) => item !== dimension);
		const excludedDimensions = that
			.getExcludedDimensions()
			.filter((item) => item !== dimension);

		my.updateDimensionSpecification(includedDimensions, excludedDimensions);
	};

	my.includeDimension = function () {
		const dimension = my.tree.selectedNode();
		if (dimension) {
			const includedDimensions = that.getIncludedDimensions();
			includedDimensions.push(dimension);

			my.updateDimensionSpecification(
				includedDimensions,
				that.getExcludedDimensions(),
			);
		}
	};

	my.excludeDimension = function () {
		const dimension = my.tree.selectedNode();
		if (dimension) {
			const excludedDimensions = that.getExcludedDimensions();
			excludedDimensions.push(dimension);

			my.updateDimensionSpecification(
				that.getIncludedDimensions(),
				excludedDimensions,
			);
		}
	};

	my.updateDimensionSpecification = function (
		includedDimensions,
		excludedDimensions,
	) {
		my.dimensionSpecification = my.makeDimensionSpecification(
			includedDimensions,
			excludedDimensions,
		);

		that.update();
		that.onDimensionsSelected.trigger(my.dimensionSpecification);
	};

	my.makeDimensionSpecification = function (
		includedDimensions,
		excludedDimensions,
	) {
		const includedDimensionSpecification =
			my.makeIncludedDimensionSpecification(includedDimensions);
		const excludedDimensionSpecification =
			my.makeExcludedDimensionSpeicification(excludedDimensions);

		if (includedDimensionSpecification && excludedDimensionSpecification) {
			return new AndSpecification({
				specifications: [
					includedDimensionSpecification,
					excludedDimensionSpecification,
				],
			});
		}

		return includedDimensionSpecification || excludedDimensionSpecification;
	};

	my.makeIncludedDimensionSpecification = function (dimensions) {
		const specifications = dimensions.map(
			(dim) => new DimensionSpecification({ dimensionId: dim.getId() }),
		);
		switch (dimensions.length) {
			case 0: {
				return null;
			}
			case 1: {
				return specifications[0];
			}
			default: {
				return new OrSpecification({ specifications });
			}
		}
	};

	my.makeExcludedDimensionSpeicification = function (dimensions) {
		const specification = my.makeIncludedDimensionSpecification(dimensions);
		return specification && new NotSpecification({ specification });
	};
});

OrSpecification.prototype.canBeEdited = function () {
	return this._specifications.every((spec) => spec.isLeaf());
};

OrSpecification.prototype.includedDimensionIds = function () {
	return this._specifications.map((spec) => spec.getDimensionId());
};

OrSpecification.prototype.excludedDimensionIds = function () {
	return [];
};

AndSpecification.prototype.canBeEdited = function () {
	return (
		this._specifications.length === 2 &&
		this._specifications[0].canBeEdited() &&
		this._specifications[1].canBeEdited() &&
		this._specifications[1].isNot()
	);
};

AndSpecification.prototype.includedDimensionIds = function () {
	return this._specifications[0].includedDimensionIds();
};

AndSpecification.prototype.excludedDimensionIds = function () {
	return this._specifications[1].excludedDimensionIds();
};

NotSpecification.prototype.canBeEdited = function () {
	return this._specification.isOr() || this._specification.isLeaf();
};

NotSpecification.prototype.includedDimensionIds = function () {
	return this._specification.excludedDimensionIds();
};

NotSpecification.prototype.excludedDimensionIds = function () {
	return this._specification.includedDimensionIds();
};

DimensionSpecification.prototype.canBeEdited = function () {
	return true;
};

DimensionSpecification.prototype.includedDimensionIds = function () {
	return [this._dimensionId];
};

DimensionSpecification.prototype.excludedDimensionIds = function () {
	return [];
};

export default multipleDimensionsPickerWidget;
